import React, {memo, useContext, useEffect, useState} from 'react';
import MergeGridItem from '../../../../shared/components/mergeBoard/MergeGridItem';
import {PlayerContext} from "../../../../shared/contexts/playerContext";
import {useMergeEventBlockMainData, useMergeEventData} from "../../../logs/useTableData";
import IsLoading from "../../../../shared/components/IsLoading";
import ErrorPage from "../../../../shared/components/ErrorPage";
import {Button, Tooltip} from 'antd';
import CollectionModal from './CollectionModal';
import MergeInventoryList from "../../../../shared/components/mergeBoard/MergeInventoryList";
import {MERGE_EVENT_TYPE} from "../../../../shared/utils/enum";

const BOSS_IMG_PATH = '/UI/Images/Event/BossRaid/';
const COLLECTION_IMG = '/UI/Images/Event/FinalBlockMergeEvent/FinalBlockMergeEvent01/MergeEvent_HalloweenetbBookdd.png';

const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: '500px',
    margin: '0 auto',
    position: 'relative',
};

const boardStyle = {
    width: '100%',
    padding: '5px',
    gap: '2px',
    backgroundColor: '#cceeff',
    borderRadius: '16px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
    boxSizing: 'border-box',
    minHeight: 420,
    minWidth: 300,
    position: 'relative',
};

const emptyMessageStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: '#888',
    fontSize: 18,
    zIndex: 2,
    pointerEvents: 'none',
};

const emptySlotStyle = {
    width: '100%',
    aspectRatio: '1 / 1',
    background: '#f5f5f5',
    border: '1px solid #e0e0e0',
    borderRadius: 6,
};

const collectionBtnStyle = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    width: 80,
    height: 80,
};

const CannonGauge = ({gaugePoint, maxGauge}) => {
    const percent = maxGauge > 0 ? (gaugePoint / maxGauge) * 100 : 0;
    return (
        <div style={{display: 'flex', alignItems: 'center', gap: 8, marginTop: 6}}>
            <div style={{
                width: 48, height: 48, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: '#f0f0f0', border: '1px solid #d0d0d0',
                borderRadius: 6, fontSize: 13, fontWeight: 'bold', color: '#444',
            }}>
                대포
            </div>
            <div style={{flex: 1}}>
                <div style={{fontSize: 11, color: '#666', marginBottom: 3}}>
                    {gaugePoint.toLocaleString()} / {maxGauge.toLocaleString()}
                </div>
                <div style={{height: 8, backgroundColor: '#e0e0e0', borderRadius: 4}}>
                    <div style={{
                        width: `${Math.min(percent, 100)}%`, height: '100%',
                        backgroundColor: '#ff8f00', borderRadius: 4, transition: 'width 0.3s',
                    }}/>
                </div>
            </div>
        </div>
    );
};

const MergeEventGridBoss = memo(() => {
    const {playerData} = useContext(PlayerContext);
    const {
        data: mergeEventMainData,
        isLoading: isMergeEventMainLoading,
        error: mergeEventMainDataError,
    } = useMergeEventBlockMainData();
    const {
        data: mergeEventData,
        isLoading: isMergeEventDataLoading,
        error: mergeEventDataError,
    } = useMergeEventData();

    const [mergeEvent, setMergeEvent] = useState(playerData?.playerInfo?.mergeEventData || {});
    const [isCollectionModalOpen, setIsCollectionModalOpen] = useState(false);
    const [length, setLength] = useState(6);
    const [bossData, setBossData] = useState(mergeEventData?.boss || {});
    const [isRaid, setIsRaid] = useState(false);
    const [currentBossIndex, setCurrentBossIndex] = useState(0);
    const [currentStage, setCurrentStage] = useState(1);
    const [totalStages, setTotalStages] = useState(1);

    const activeState = playerData?.playerInfo?.mergeEventData?.ActiveMergeEventState;
    const collection = activeState?.collectionData || [];
    const blocks = activeState?.boardData?.slotData || [];

    useEffect(() => {
        setMergeEvent(playerData?.playerInfo?.mergeEventData || {});
    }, [playerData]);

    useEffect(() => {
        setLength(mergeEvent.ActiveMergeEventState?.boardData?.slotData?.[0]?.length ?? 6);
    }, [mergeEvent]);

    // mergeEventData 로드 후 레이드 여부 판단 및 현재 보스 인덱스 계산
    useEffect(() => {
        if (!mergeEventData) return;

        const boss = mergeEventData.boss || {};
        const master = mergeEventData.master || {};
        const roundValues = mergeEventData.round ? Object.values(mergeEventData.round) : [];
        const activeId = activeState?.id;
        const raid = master[activeId]?.mergeEventType === MERGE_EVENT_TYPE.BOSS_RAID;

        setBossData(boss);
        setIsRaid(raid);

        if (!raid) return;

        const filteredRounds = roundValues.filter(r => r.mergeEventIdx === activeId);
        const roundRewardInfos = activeState?.roundRewardInfos || [];
        const lastRewardIndex = roundRewardInfos.length > 0 ? roundRewardInfos[roundRewardInfos.length - 1] : null;
        let nextRoundIndex;
        if (lastRewardIndex === null) {
            nextRoundIndex = filteredRounds[0]?.index;
        } else if (lastRewardIndex === 0) {
            return;
        } else {
            nextRoundIndex = filteredRounds.find(e => e.index === lastRewardIndex + 1)?.index ?? null;
        }

        if (nextRoundIndex == null) return;
        setCurrentBossIndex(nextRoundIndex);
        setTotalStages(filteredRounds.length);
        setCurrentStage(filteredRounds.findIndex(r => r.index === nextRoundIndex) + 1);
    }, [mergeEventData]);

    const isLoading = isMergeEventMainLoading || isMergeEventDataLoading;
    const error = mergeEventMainDataError || mergeEventDataError;

    if (isLoading) return <IsLoading/>;
    if (error) return <ErrorPage error={error}/>;
    if (!isRaid) return null;

    const maxHP = bossData[currentBossIndex]?.bossHP || 0;
    const currentHP = Math.max(0, maxHP - (activeState?.eventPoint || 0));
    const hpPercent = maxHP > 0 ? (currentHP / maxHP) * 100 : 0;

    const stageRounds = mergeEventData?.round
        ? Object.values(mergeEventData.round).filter(r => r.mergeEventIdx === activeState?.id)
        : [];
    const clearedSet = new Set(activeState?.roundRewardInfos || []);

    return (
        <div>
            <div style={containerStyle}>
                <div style={{position: 'relative', width: 100}}>
                    <img
                        src={`${BOSS_IMG_PATH}${bossData[currentBossIndex]?.bossIconPath}.png`}
                        alt="Boss"
                        style={{width: 100, height: 100, objectFit: 'contain', display: 'block'}}
                    />
                    <div style={{position: 'absolute', bottom: 0, left: 0, right: 0, height: 8, backgroundColor: '#e0e0e0', borderRadius: 4}}>
                        <div style={{width: `${hpPercent}%`, height: '100%', backgroundColor: '#e53935', borderRadius: 4, transition: 'width 0.3s'}}/>
                    </div>
                </div>
                <div style={{fontSize: 12, color: '#555', marginTop: 4, textAlign: 'center'}}>
                    HP {currentHP.toLocaleString()} / {maxHP.toLocaleString()}
                </div>
                <div style={{fontSize: 12, color: '#888', marginBottom: 4, textAlign: 'center'}}>
                    {currentStage} / {totalStages} 단계
                </div>
                <div style={{display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 8}}>
                    {stageRounds.map((round, i) => {
                        const cleared = clearedSet.has(round.index);
                        const isCurrent = round.index === currentBossIndex;
                        return (
                            <div key={round.index} style={{
                                width: 24, height: 24, borderRadius: '50%',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 10, fontWeight: 'bold',
                                backgroundColor: cleared ? '#4caf50' : isCurrent ? '#1890ff' : '#e0e0e0',
                                color: cleared || isCurrent ? '#fff' : '#999',
                                outline: isCurrent ? '2px solid #0050b3' : 'none',
                                outlineOffset: 1,
                            }}>
                                {cleared ? '✓' : i + 1}
                            </div>
                        );
                    })}
                </div>

                <div>
                    <Tooltip title="컬렉션">
                        <Button
                            type="text"
                            style={collectionBtnStyle}
                            onClick={() => setIsCollectionModalOpen(true)}
                        >
                            <img
                                src={COLLECTION_IMG}
                                alt="Collection"
                                style={{width: 80, height: 80, display: 'block', objectFit: 'contain'}}
                            />
                        </Button>
                    </Tooltip>
                    <MergeInventoryList
                        mergeInventories={activeState?.boardData?.blockDepot}
                        mergeMainData={mergeEventMainData}
                    />
                    <CannonGauge
                        gaugePoint={activeState?.BossRaidState?.gaugePoint || 0}
                        maxGauge={mergeEventData?.cannon?.[activeState?.id]?.needItemValue || 0}
                    />
                </div>

                <div style={{...boardStyle, display: 'grid', gridTemplateColumns: `repeat(${length}, 1fr)`}}>
                    {blocks.length === 0 ? (
                        <>
                            <div style={emptyMessageStyle}>데이터가 없습니다.</div>
                            {Array.from({length: 48}).map((_, idx) => (
                                <div key={idx} style={emptySlotStyle}/>
                            ))}
                        </>
                    ) : (
                        blocks.flat().map((item, index) => (
                            <MergeGridItem
                                key={item?.eventBlock?.slotIndex ?? `empty-${index}`}
                                item={item}
                                mergeMainData={mergeEventMainData}
                                playerData={playerData}
                            />
                        ))
                    )}
                </div>
            </div>

            <CollectionModal
                open={isCollectionModalOpen}
                onClose={() => setIsCollectionModalOpen(false)}
                collection={collection}
                mergeEventMainData={mergeEventMainData}
            />
        </div>
    );
});

export default MergeEventGridBoss;